/**
 * 群聊管理
 */
import { Card, Form, Button, Input, message, Modal, Popconfirm, Cascader } from "antd";
import { PlusOutlined, DeleteOutlined, ReloadOutlined } from "@ant-design/icons";
import styles from "@/styles/pageContent.module.scss";
import { getWorkBotChatList, createWorkBotChat, deleteWorkBotChat, WorkBotChatListRequestParams, deleteWorkBotAlarmBatch, createWorkBotAlarm } from "@/service/workBot";
import { useState } from "react";
import CommonTable from "@/components/commonTable";
import { useTable } from "@/utils/useTable";
import { formatTime } from "@/utils/formatTime";
import CategoryTreeSelect from "@/components/categoryTreeSelect";
import { jtDeviceAlarmTypeData } from "@/constants/jtDeviceAlarm";
import { displayJtDeviceAlarmV2, getAlarmTypeCodeFromValue, getAlarmTypeValueFromCode } from "@/utils/jtDeviceAlarm";
import { log } from "console";


function WorkBotChat() {
    const [createForm] = Form.useForm();
    const [editAlarmForm] = Form.useForm();
    const [createModalVisible, setCreateModalVisible] = useState(false);
    const [createLoading, setCreateLoading] = useState(false);
    const [editAlarmModalVisible, setEditAlarmModalVisible] = useState(false);
    const [currentRecord, setCurrentRecord] = useState<any>(null);

    // 使用 useTable 钩子管理表格数据
    const { page, limit, loading, data, totalCount, onPageChange, getData } = useTable({
        requestFunc: getWorkBotChatList,
        initRequest: true,
    });



    // 创建聊天群
    const handleCreate = async (values: WorkBotChatListRequestParams) => {
        setCreateLoading(true);
        try {
            const params = {
                ...values,
                workbotAlarms: [] // 确保 workbotAlarms 可以为 null
            };
            const res = await createWorkBotChat(params);
            if (res.data?.code === 0) {
                message.success('创建成功');
                setCreateModalVisible(false);
                createForm.resetFields();
                getData(); // 刷新表格数据
            }

        } catch (error) {
            message.error('创建失败');
        } finally {
            setCreateLoading(false);
        }
    };

    // 删除聊天群
    const handleDelete = async (id: number) => {
        try {
            await deleteWorkBotChat(id);
            message.success('删除成功');
            getData(); // 刷新表格数据
        } catch (error) {
            message.error('删除失败');
        }
    };

    // 编辑告警内容
    const handleEditAlarm = (record: any) => {
        setCurrentRecord(record);
        // 将当前记录的告警数据转换为Cascader需要的格式
        const alarmValuesArr = record.workbotAlarms?.map((alarm: any) => [alarm.alarmType, alarm.warningType]) || [];

        const alarmValues = alarmValuesArr.map((item:any)=>getAlarmTypeValueFromCode({ alarmType: item[0], warningType: item[1] }));
        editAlarmForm.setFieldsValue({
            alarms: alarmValues
        });
        console.log(alarmValues);
        setEditAlarmModalVisible(true);
    };

    // 确认编辑告警
    const handleConfirmEditAlarm = async (values: any) => {
        const formValues = editAlarmForm.getFieldValue('alarms');
        const pairList = formValues.map(getAlarmTypeCodeFromValue).map((item:Array<number>)=>{ return { first: item[0], second: item[1] } });
        const params = {
            pairList: pairList,
            workbotChatId: currentRecord?.id || null
        };

        try{
            //先调用接口删除之前的告警
            const delRes = await deleteWorkBotAlarmBatch(currentRecord?.id);
            if (delRes.data?.code === 0) {
                // 删除成功后再创建新的告警
                const createRes = await createWorkBotAlarm(params);
                if (createRes.data?.code === 0) {
                    message.success('编辑成功');
                    setEditAlarmModalVisible(false);
                    editAlarmForm.resetFields();
                    getData(); // 刷新表格数据
                }
            }
        } catch (error) {
            message.error('编辑失败');
        }
    };

    // 表格列配置
    const columns = [

        {
            title: '分组',
            dataIndex: 'cateTitle',
            key: 'cateTitle',
            width: 250,
            ellipsis: true,
        },
        {
            title: 'ID',
            dataIndex: 'id',
            key: 'id',
            width: 80,
        },
        {
            title: '群聊名称',
            dataIndex: 'groupChatName',
            key: 'groupChatName',
            width: 200,
            ellipsis: true,
        },

        {
            title: '创建时间',
            dataIndex: 'createdAt',
            key: 'createdAt',
            width: 180,
            render: (text: string) => text ? formatTime(text) : '-',
        },
        {
            title: '更新时间',
            dataIndex: 'updatedAt',
            key: 'updatedAt',
            width: 180,
            render: (text: string) => text ? formatTime(text) : '-',
        },
        {
            title: '群聊告警',
            dataIndex: 'workbotAlarms',
            key: 'workbotAlarms',
            width: 200,
            render: (alarms: any[]) => {
                return alarms.map(alarm => (
                    <span key={alarm.id} style={{ marginRight: 8 }}>
                        {displayJtDeviceAlarmV2({
                            alarmType: alarm.alarmType,
                            warningType: alarm.warningType
                        })}
                    </span>
                ));
            }
        },
        {
            title: '操作',
            key: 'action',
            width: 120,
            fixed: 'right' as const,
            render: (_: any, record: any) => (
                <div>
                    <Button
                        type="link"
                        size="small"
                        onClick={() => handleEditAlarm(record)}
                        style={{ marginRight: 8 }}
                    >
                        编辑告警
                    </Button>
                    <Popconfirm
                        title="确定要删除此聊天群吗？"
                        onConfirm={() => handleDelete(record.id)}
                        okText="确定"
                        cancelText="取消"
                    >
                        <Button
                            type="link"
                            danger
                            icon={<DeleteOutlined />}
                            size="small">
                            删除群聊
                        </Button>
                    </Popconfirm>
                </div>
            ),
        },
    ];

    return (
        <Card bordered={false} className={styles.fixPageContentBox}>
            {/* 操作按钮 */}
            <div style={{ marginBottom: 16 }}>
                <Button
                    type="primary"
                    icon={<PlusOutlined />}
                    onClick={() => setCreateModalVisible(true)}
                    style={{ marginRight: 8 }}
                >
                    新建
                </Button>
                <Button
                    icon={<ReloadOutlined />}
                    onClick={() => getData()}
                >
                    刷新
                </Button>
            </div>

            {/* 表格 */}
            <CommonTable
                columns={columns}
                dataSource={data}
                loading={loading}
                pagination={{
                    current: page,
                    pageSize: limit,
                    total: totalCount,
                    onChange: onPageChange,
                    showSizeChanger: true,
                    showQuickJumper: true,
                    showTotal: (total) => `共 ${total} 条数据`,
                }}
                rowKey="id"
                scroll={{ x: 1000 }}
            />

            {/* 创建弹窗 */}
            <Modal
                title="新建聊天群"
                open={createModalVisible}
                onOk={() => createForm.submit()}
                onCancel={() => {
                    setCreateModalVisible(false);
                    createForm.resetFields();
                }}
                confirmLoading={createLoading}
                destroyOnClose
            >
                <Form
                    form={createForm}
                    onFinish={handleCreate}
                    layout="vertical"
                >
                    <Form.Item
                        name="cateId"
                        label="分组ID"
                        rules={[{ required: false }]}
                    >
                        <CategoryTreeSelect />
                    </Form.Item>

                    <Form.Item
                        name="groupChatName"
                        label="群聊名称"
                        rules={[{ required: true, message: '请输入群聊名称' }]}
                    >
                        <Input placeholder="请输入群聊名称" />
                    </Form.Item>
                </Form>
            </Modal>

            {/* 编辑告警内容弹窗 */}
            <Modal
                title="编辑告警"
                open={editAlarmModalVisible}
                onOk={() => editAlarmForm.submit()}
                onCancel={() => {
                    setEditAlarmModalVisible(false);
                    setCurrentRecord(null);
                    editAlarmForm.resetFields();
                }}
                okText="确认"
                cancelText="取消"
                destroyOnClose
                width={800}
            >
                <Form
                    form={editAlarmForm}
                    layout="vertical"
                    onFinish={handleConfirmEditAlarm}
                >
                    <Form.Item
                        name="alarms"
                        label="告警类型"
                    >
                        <Cascader
                            multiple
                            options={jtDeviceAlarmTypeData}
                            maxTagCount="responsive"
                            allowClear
                            showCheckedStrategy={Cascader.SHOW_CHILD}
                            showSearch
                            placeholder="请选择告警类型"
                        />
                    </Form.Item>
                </Form>
            </Modal>
        </Card>
    )
}

export default WorkBotChat;